import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { IndianRupee, Search, Calendar, TrendingUp, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import ListPageHeader from '../../components/shared/ListPageHeader';
import { staffSelfAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';

const MyCollectionsPage = () => {
  const { businessId } = useParams();
  const [loading, setLoading] = useState(true);
  const [collections, setCollections] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [stats, setStats] = useState({
    totalCollected: 0,
    todayCollected: 0,
    cashAmount: 0,
    upiAmount: 0,
  });

  const fetchCollections = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await staffSelfAPI.getMyCollections();
      setCollections(data.collections || []);
      setStats(data.stats || {
        totalCollected: 0,
        todayCollected: 0,
        cashAmount: 0,
        upiAmount: 0,
      });
    } catch (error) {
      console.error('Error fetching collections:', error);
      toast.error(getApiErrorMessage(error, 'Failed to load collections'));
      setCollections([]);
      setStats({ totalCollected: 0, todayCollected: 0, cashAmount: 0, upiAmount: 0 });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCollections();
  }, [fetchCollections]);

  const formatCompactCurrency = (amount) => {
    if (!amount) return '₹0';
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount.toFixed(0)}`;
  };

  const formatCurrency = (amount) => {
    return `₹${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '---';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
    });
  };

  // Client-side date filtering
  const getFilteredByDate = (items) => {
    if (dateFilter === 'all') return items;
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return items.filter(c => {
      const d = new Date(c.payment_date || c.created_at);
      if (isNaN(d)) return false;
      if (dateFilter === 'today') return d >= todayStart;
      if (dateFilter === 'week') {
        const weekStart = new Date(todayStart);
        weekStart.setDate(weekStart.getDate() - weekStart.getDay());
        return d >= weekStart;
      }
      if (dateFilter === 'month') {
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        return d >= monthStart;
      }
      return true;
    });
  };

  const filteredCollections = getFilteredByDate(
    collections.filter(c => {
      if (!searchQuery) return true;
      const search = searchQuery.toLowerCase();
      return (
        c.party_name?.toLowerCase().includes(search) ||
        (c.receipt_number || c.receipt_no || '').toLowerCase().includes(search)
      );
    })
  );

  return (
    <div className="h-full flex flex-col" data-testid="my-collections-page">
      <ListPageHeader
        title="My Collections"
        subtitle="Track payments collected from retailers"
        stats={[
          { icon: Receipt, label: 'Total', value: collections.length, color: 'slate' },
          { icon: IndianRupee, label: 'Total Amount', value: formatCompactCurrency(stats.totalCollected || 0), color: 'emerald' },
          { icon: Calendar, label: 'Today', value: formatCompactCurrency(stats.todayCollected || 0), color: 'blue' },
          { icon: TrendingUp, label: 'Cash', value: formatCompactCurrency(stats.cashAmount || 0), color: 'purple' },
        ]}
      />

      {/* Controls Bar */}
      <div className="flex-shrink-0 px-2 pt-2">
        <div className="bg-white rounded-md border border-slate-200/60 shadow-sm p-3">
          <div className="flex items-center gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search by retailer or receipt number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full max-w-md pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[13px] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
              />
            </div>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[13px] text-slate-700 focus:ring-2 focus:ring-teal-500/20 focus:outline-none"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table Area */}
      <div className="flex-1 overflow-hidden flex flex-col px-2 pb-2 pt-2">
        <div className="flex-1 flex flex-col bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
                <p className="text-[13px] text-slate-500 font-normal">Loading collections...</p>
              </div>
            </div>
          ) : filteredCollections.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center">
              <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                <IndianRupee className="w-7 h-7 text-slate-400" />
              </div>
              <p className="text-[15px] font-medium text-slate-600">
                {searchQuery ? 'No collections found' : 'No collections yet'}
              </p>
              <p className="text-[13px] text-slate-400 mt-1">
                {searchQuery ? 'Try adjusting your search' : 'Start collecting payments from retailers'}
              </p>
            </div>
          ) : (
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="flex-1 overflow-y-auto">
                <table className="tl-table table-fixed w-full">
                  <colgroup>
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '25%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '15%' }} />
                  </colgroup>
                  <thead className="sticky top-0 z-10">
                    <tr>
                      <th className="tl-th tl-th-teal">Receipt #</th>
                      <th className="tl-th tl-th-teal">Retailer</th>
                      <th className="tl-th tl-th-teal">Date</th>
                      <th className="tl-th tl-th-teal">Mode</th>
                      <th className="tl-th tl-th-teal tl-th-right">Amount</th>
                      <th className="tl-th tl-th-teal">Status</th>
                    </tr>
                  </thead>
                  <tbody className="animate-stagger">
                    {filteredCollections.map((collection, index) => (
                      <tr
                        key={collection.id}
                        className={`tl-tr hover:bg-teal-50/40 animate-tableRowFadeIn ${index % 2 === 0 ? '' : 'bg-[#FAFBFC]'}`}
                        style={{ animationDelay: `${Math.min(index * 30, 300)}ms` }}
                      >
                        <td className="tl-td">
                          <span className="tl-cell-mono">{collection.receipt_number || collection.receipt_no || '---'}</span>
                        </td>
                        <td className="tl-td">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                              <span className="text-[12px] font-medium text-teal-700">
                                {collection.party_name?.charAt(0)?.toUpperCase() || '?'}
                              </span>
                            </div>
                            <span className="tl-cell-primary truncate">{collection.party_name || 'Unknown'}</span>
                          </div>
                        </td>
                        <td className="tl-td tl-cell-regular">
                          {formatDateTime(collection.payment_date || collection.created_at)}
                        </td>
                        <td className="tl-td">
                          <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-600">
                            {collection.payment_mode || 'cash'}
                          </span>
                        </td>
                        <td className="tl-td tl-td-right">
                          <span className="tl-cell-amount tl-cell-amount-positive">
                            {formatCurrency(collection.amount_received || collection.amount)}
                          </span>
                        </td>
                        <td className="tl-td">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-50 text-emerald-700">
                            <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                            Collected
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Footer */}
              <div className="flex-shrink-0 px-4 py-3 bg-slate-50/80 border-t border-slate-100 flex items-center justify-between text-[12px] text-slate-500">
                <p>Showing <span className="font-medium text-slate-700">{filteredCollections.length}</span> collections</p>
                <p>Total: <span className="font-medium text-emerald-600">{formatCompactCurrency(stats.totalCollected || 0)}</span></p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyCollectionsPage;
